import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { signIn, confirmSignIn, getCurrentUser, signOut } from 'aws-amplify/auth'
import { Alert, Button, Card, Col, Form, Nav, Row, Spinner } from 'react-bootstrap'
import { LinkContainer } from 'react-router-bootstrap'
import { QRCodeSVG } from 'qrcode.react'

function AdminLogin() {
  const navigate = useNavigate()
  const [authState, setAuthState] = useState('loading') // 'loading' | 'authenticated' | 'unauthenticated'
  const [step, setStep] = useState('password') // 'password' | 'totp-setup' | 'totp'
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [totpSetupUri, setTotpSetupUri] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getCurrentUser()
      .then(() => setAuthState('authenticated'))
      .catch(() => setAuthState('unauthenticated'))
  }, [])

  async function handleSignOut() {
    await signOut()
    navigate('/')
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { nextStep } = await signIn({ username, password })
      if (nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        setTotpSetupUri(nextStep.totpSetupDetails.getSetupUri('OldAwg', username).toString())
        setStep('totp-setup')
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setStep('totp')
      } else if (nextStep.signInStep === 'DONE') {
        navigate('/admin/charities')
      }
    } catch (err) {
      setError(err.message || 'Sign in failed. Please check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  async function handleTotpSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { nextStep } = await confirmSignIn({ challengeResponse: totpCode })
      if (nextStep.signInStep === 'DONE') {
        navigate('/admin/charities')
      }
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (authState === 'loading') {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Checking authentication…</span>
        </Spinner>
      </div>
    )
  }

  if (authState === 'authenticated') {
    return (
      <Row className="justify-content-center pt-5">
        <Col xs={12} sm={9} md={6} lg={5}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4 p-md-5">
              <h1 className="h4 fw-bold text-center mb-4">Admin</h1>
              <Nav className="flex-column gap-2 mb-4">
                <LinkContainer to="/admin/charities">
                  <Nav.Link>Charities</Nav.Link>
                </LinkContainer>
                <LinkContainer to="/admin/sre">
                  <Nav.Link>SRE Events</Nav.Link>
                </LinkContainer>
                <LinkContainer to="/admin/stock">
                  <Nav.Link>Stock Pricing</Nav.Link>
                </LinkContainer>
                <LinkContainer to="/admin/investments">
                  <Nav.Link>Investments</Nav.Link>
                </LinkContainer>
                <div className="ms-0 mt-1">
                  <div className="text-muted small fw-semibold text-uppercase mb-1 ps-3" style={{ letterSpacing: '0.05em' }}>
                    S&amp;P 500
                  </div>
                  <Nav className="flex-column gap-1">
                    <LinkContainer to="/admin/snp500">
                      <Nav.Link className="ps-4 py-1">S&amp;P 500 List</Nav.Link>
                    </LinkContainer>
                    <LinkContainer to={{ pathname: '/admin/snp500', search: '?tab=fixes' }}>
                      <Nav.Link className="ps-4 py-1">Fixes</Nav.Link>
                    </LinkContainer>
                    <LinkContainer to={{ pathname: '/admin/snp500', search: '?tab=no-data' }}>
                      <Nav.Link className="ps-4 py-1">No-Data Symbols</Nav.Link>
                    </LinkContainer>
                  </Nav>
                </div>
              </Nav>
              <Button variant="outline-dark" className="w-100" onClick={handleSignOut}>
                Sign Out
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    )
  }

  return (
    <Row className="justify-content-center pt-5">
      <Col xs={12} sm={9} md={6} lg={5}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4 p-md-5">
            <h1 className="h4 fw-bold text-center mb-4">Admin Sign In</h1>

            {error && (
              <Alert variant="danger" className="small py-2">
                {error}
              </Alert>
            )}

            {step === 'password' && (
              <Form onSubmit={handlePasswordSubmit} noValidate>
                <Form.Group className="mb-3" controlId="username">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="password">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </Form.Group>

                <Button type="submit" variant="dark" className="w-100" disabled={loading}>
                  {loading ? <Spinner size="sm" animation="border" aria-hidden="true" /> : 'Sign In'}
                </Button>
              </Form>
            )}

            {step === 'totp-setup' && (
              <Form onSubmit={handleTotpSubmit} noValidate>
                <p className="text-muted small mb-3">
                  Scan this QR code with Google Authenticator, then enter the 6-digit code below.
                </p>

                <div className="d-flex justify-content-center mb-3">
                  <QRCodeSVG value={totpSetupUri} size={180} />
                </div>

                <p className="text-muted small mb-3">
                  After scanning, enter the 6-digit code to complete setup.
                </p>

                <Form.Group className="mb-4" controlId="totpCode">
                  <Form.Label>Authenticator Code</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    autoComplete="one-time-code"
                    autoFocus
                    required
                  />
                </Form.Group>

                <Button type="submit" variant="dark" className="w-100" disabled={loading}>
                  {loading ? <Spinner size="sm" animation="border" aria-hidden="true" /> : 'Verify'}
                </Button>
              </Form>
            )}

            {step === 'totp' && (
              <Form onSubmit={handleTotpSubmit} noValidate>
                <p className="text-muted small mb-3">
                  Open Google Authenticator and enter the 6-digit code.
                </p>

                <Form.Group className="mb-4" controlId="totpCode">
                  <Form.Label>Authenticator Code</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    autoComplete="one-time-code"
                    autoFocus
                    required
                  />
                </Form.Group>

                <Button type="submit" variant="dark" className="w-100" disabled={loading}>
                  {loading ? <Spinner size="sm" animation="border" aria-hidden="true" /> : 'Verify'}
                </Button>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default AdminLogin
